import { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploadField from "@/components/ImageUploadField";

const CATEGORY_OPTIONS = [
  "Corporate",
  "Anime",
  "Vintage",
  "Cyberpunk",
  "Editorial",
  "Minimalist",
  "Street",
  "Fantasy",
  "Cottagecore",
  "Other",
];

/**
 * Modal to edit an existing prompt document.
 * Pre-fills every field, submits via PUT /api/admin/prompts/{id} (updates in
 * place — never duplicates), then calls onSaved to refresh the parent list.
 */
export default function EditPromptModal({ prompt, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    image_url: "",
    prompt_text: "",
    style_category: "",
    explanation_text: "",
    how_to_use: "",
    parameters: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!prompt) return;
    setForm({
      title: prompt.title || "",
      image_url: prompt.image_url || "",
      prompt_text: prompt.prompt_text || "",
      style_category: prompt.style_category || "",
      explanation_text: prompt.explanation_text || "",
      how_to_use: prompt.how_to_use || "",
      parameters: prompt.parameters || "",
    });
  }, [prompt]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!prompt) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/prompts/${prompt.id}`, form);
      toast.success("Prompt updated", { description: data.title });
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      toast.error("Update failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-prompt-modal"
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-2xl my-8 rounded-2xl border border-white/10 bg-[#0E0E12] shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <div className="font-mono-label text-[#00E5FF]">Edit prompt</div>
            <div className="font-display text-lg mt-0.5 line-clamp-1">
              {prompt.title}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-close-btn"
            aria-label="Close"
            className="h-9 w-9 rounded-full text-white/60 hover:text-white hover:bg-white/5 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="font-mono-label text-white/60">Title</label>
            <input
              required
              data-testid="edit-title"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <div>
            <label className="font-mono-label text-white/60">Image</label>
            <div className="mt-2">
              <ImageUploadField
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
              />
            </div>
          </div>

          <div>
            <label className="font-mono-label text-white/60">Style Category</label>
            <input
              required
              list="edit-category-suggestions"
              data-testid="edit-category"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
              value={form.style_category}
              onChange={(e) => setForm({ ...form, style_category: e.target.value })}
            />
            <datalist id="edit-category-suggestions">
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="font-mono-label text-white/60">Prompt Text</label>
            <textarea
              required
              rows={4}
              data-testid="edit-prompt-text"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 font-mono text-sm"
              value={form.prompt_text}
              onChange={(e) => setForm({ ...form, prompt_text: e.target.value })}
            />
          </div>

          <div>
            <label className="font-mono-label text-white/60">
              Explanation (100–200 words)
            </label>
            <textarea
              required
              rows={5}
              data-testid="edit-explanation"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
              value={form.explanation_text}
              onChange={(e) => setForm({ ...form, explanation_text: e.target.value })}
            />
          </div>

          <div>
            <label className="font-mono-label text-white/60">
              How to Use <span className="text-white/40 normal-case">(optional)</span>
            </label>
            <textarea
              rows={3}
              data-testid="edit-how-to-use"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
              value={form.how_to_use}
              onChange={(e) => setForm({ ...form, how_to_use: e.target.value })}
            />
          </div>

          <div>
            <label className="font-mono-label text-white/60">
              Parameters <span className="text-white/40 normal-case">(optional)</span>
            </label>
            <input
              type="text"
              data-testid="edit-parameters"
              placeholder="Model: Gemini 2.5, Aspect: 4:5"
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm font-mono"
              value={form.parameters}
              onChange={(e) => setForm({ ...form, parameters: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/5 bg-black/20 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="edit-cancel-btn"
            className="px-5 py-2 rounded-full text-sm text-white/70 hover:text-white border border-white/15 hover:border-white/30 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white bg-[#E0007A] hover:bg-[#ff1493] glow-magenta transition-transform hover:scale-[1.02] disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
